import React, { useEffect, useState } from 'react';

import { Select } from '@aline/common';
import { ButtonBase, InputAdornment, InputBase, Typography } from '@mui/material';
import type { AnswerOptionItemVariant } from 'src/components';
import { CloseIcon } from 'src/components/icons/CloseIcon';
import { RadioSelectedIcon } from 'src/components/icons/RadioSelectedIcon';
import { RadioUnselectedIcon } from 'src/components/icons/RadioUnselectedIcon';
import { Apollo } from 'src/gql/generated';
import { makeStyles } from 'tss-react/mui';

import type { AnswerOptionItem } from './AnswerOptionsList';
import { CheckboxUnselectedIcon } from '../icons/CheckboxUnselectedIcon';

const useStyles = makeStyles<StyleProps>()((theme, { variant }) => ({
  closeIcon: {
    height: 12,
    width: 12,
  },
  optionInput: {
    alignItems:
      variant === Apollo.FieldVariant.Radiobutton ||
      variant === Apollo.MetadataVariant.RadioOptions ||
      variant === Apollo.FieldVariant.Checkbox ||
      variant === Apollo.MetadataVariant.Checkbox
        ? undefined
        : ('baseline' as const),
  },
  optionInputWrapper: {
    '&:focus-within': {
      border: `1px solid ${theme.appColors.primary}`,
    },
    alignItems: 'center',
    backgroundColor: theme.appColors.background,
    boxSizing: 'border-box' as const,
    display: 'flex',
    height: 48,
    padding: theme.spacing(0, 2),
    width: '100%',
  },
}));

interface StyleProps {
  variant: AnswerOptionItemVariant;
  disabled?: boolean;
}

interface AdornmentProps {
  index: number;
  variant: AnswerOptionItemVariant;
  isDefaultAnswer?: boolean;
}

function AnswerOptionStartAdornment({
  index,
  isDefaultAnswer,
  variant,
}: AdornmentProps): JSX.Element {
  switch (variant) {
    case Apollo.MetadataVariant.Options:
    case Apollo.FieldVariant.Dropdown: {
      return (
        <InputAdornment position="start">
          <Typography>{`${index + 1}.`}</Typography>
        </InputAdornment>
      );
    }
    case Apollo.MetadataVariant.RadioOptions:
    case Apollo.FieldVariant.Radiobutton: {
      return (
        <InputAdornment position="start">
          {isDefaultAnswer ? <RadioSelectedIcon /> : <RadioUnselectedIcon />}
        </InputAdornment>
      );
    }
    case Apollo.FieldVariant.Checkbox:
    case Apollo.MetadataVariant.Checkbox:
      return (
        <InputAdornment position="start">
          <CheckboxUnselectedIcon />
        </InputAdornment>
      );
  }
}

interface AnswerOptionInputProps<T extends AnswerOptionItem> {
  index: number;
  onDelete: (answerOption: T, index: number) => void;
  onUpdate: (answerOption: T, index: number) => void;
  option: T;
  variant: AnswerOptionItemVariant;
  defaultAnswerIndex?: number;
  disabled?: boolean;
}

export function AnswerOptionInput<T extends AnswerOptionItem>(
  props: AnswerOptionInputProps<T>
): JSX.Element {
  const { defaultAnswerIndex, disabled, index, onDelete, onUpdate, option, variant } = props;

  const { classes } = useStyles({ disabled, variant });
  const [showX, setShowX] = useState<boolean>(true);
  const [optionText, setOptionText] = useState<string>(option.optionText);

  // Allow re-initializing optionText if it's updated in props.option.
  useEffect(() => {
    setOptionText(option.optionText);
  }, [option.optionText]);

  return (
    <div className={classes.optionInputWrapper} role="listitem">
      <InputBase
        fullWidth
        autoComplete="off"
        className={classes.optionInput}
        data-cy={Select.document.editor.rightDrawer.fieldDetailPanel.answerOptionInput}
        disabled={disabled}
        endAdornment={
          showX && (
            <InputAdornment position="end">
              <ButtonBase
                aria-label="Remove option"
                disabled={disabled}
                // Use onMouseDown vs. onClick because the focus event fires in onMouseDown and we want to prevent the focus event on delete of an option.
                onMouseDown={(event: React.MouseEvent): void => {
                  event.preventDefault();

                  onDelete(option, index);
                }}
              >
                <CloseIcon className={classes.closeIcon} />
              </ButtonBase>
            </InputAdornment>
          )
        }
        startAdornment={
          <AnswerOptionStartAdornment
            index={index}
            isDefaultAnswer={index === defaultAnswerIndex}
            variant={variant}
          />
        }
        /**
         * Note: Do not remove placeholder.  It fixes an alignment: baseline bug present in Safari when there is no text
         * content in the input.  See https://bugs.webkit.org/show_bug.cgi?id=142968.
         */
        // eslint-disable-next-line react-js/jsx-sort-props -- Circular fix: reordering conflicts with another lint rule.
        placeholder="Enter new option..."
        value={optionText}
        onBlur={(): void => setShowX(true)}
        onChange={event => {
          setOptionText(event.target.value);

          onUpdate({ ...option, optionText: event.target.value }, index);
        }}
        onFocus={(): void => setShowX(false)}
      />
    </div>
  );
}
